import {
  addSurfaceModeAtom,
  clipBoxStateAtom,
  DEFAULT_ADD_SURFACE_MODE,
} from '@/atoms/viewer.atom';
import { selectedFeatureAtom } from '@/atoms/selection.atom';
import {
  rectFromManyPicks,
  rectFromPolygonCoords,
  rectToPolygonCoords,
} from '@/components/3D/surfaceGeometry';
import { project } from '@/core';
import { execute } from '@/graphql/execute';
import { useFeatureActions } from '@/hooks/useFeatureActions';
import { createFeatureMutation, updateFeatureMutation } from '@/queries/feature.query';
import {
  ArrowDownSquare,
  ArrowLeftSquare,
  ArrowRightSquare,
  ArrowUpSquare,
  CameraSolid,
  Check,
  CheckSquare,
  MoveHorizontal,
  MoveHorizontalSolid,
  MoveVertical,
  MoveVerticalSolid,
  Path,
  Trash,
  Undo,
} from '@mynaui/icons-react';
import type { CameraControlsImpl } from '@react-three/drei';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import * as THREE from 'three';
import type { RefObject, SetStateAction } from 'react';

import {
  FloatingToolbar,
  FloatingToolbarButton,
  FloatingToolbarSeparator,
} from './FloatingToolbar';

interface AddSurfaceToolbarProps {
  projectId: string;
  srid: number;
  orthoCamera: boolean;
  setOrthoCamera: (next: boolean) => void;
  cameraControlsRef: RefObject<CameraControlsImpl | null>;
  setPosition: (value: SetStateAction<THREE.Vector3>) => void;
  skipPositionSyncRef: { current: boolean };
}

/**
 * Padding in metres added on every axis around the surface AABB when we
 * auto-position the clipbox after a Save / OK. 1 m is the spec; small enough
 * to keep the clip tight on the surface, big enough to give a visible
 * clip-volume on near-flat surfaces (a wall's bbox is ~zero in one axis,
 * so without padding the box would render as a degenerate plane).
 */
const CLIPBOX_AUTO_PADDING_M = 1;

const SURFACE_FEATURE_TYPE = 'surface';

const WORLD_UP = new THREE.Vector3(0, 0, 1);

export const AddSurfaceToolbar = ({
  projectId,
  srid,
  orthoCamera,
  setOrthoCamera,
  cameraControlsRef,
  setPosition,
  skipPositionSyncRef,
}: AddSurfaceToolbarProps) => {
  const [mode, setMode] = useAtom(addSurfaceModeAtom);
  const selectedFeature = useAtomValue(selectedFeatureAtom);
  const setSelectedFeature = useSetAtom(selectedFeatureAtom);
  const setClipBoxState = useSetAtom(clipBoxStateAtom(projectId));
  const queryClient = useQueryClient();
  const { selectFeatureForEditing, createUpdateHandler } = useFeatureActions();
  const { t } = useTranslation('components');
  // Toggle direction per axis so a second click on the same view button flips
  // the camera to the opposite side (front ↔ back, side ↔ other side, top ↔ bottom).
  // Held in state (not a ref) so the toolbar icons can re-render to preview
  // which direction the *next* click will move the camera to.
  const [viewSign, setViewSign] = useState<Record<'top' | 'front' | 'side', 1 | -1>>({
    top: 1,
    front: 1,
    side: 1,
  });

  // Ref tracks "have we already auto-positioned the clipbox for this surface
  // session" so the effect below fires exactly once per draft. Reset whenever
  // the tool is closed so the next session starts fresh.
  const clipboxAutoPositionedRef = useRef(false);

  /**
   * Auto-position the clipbox around the just-finalised surface so the user
   * can immediately see the relevant region without manually scoping the
   * clip. The box is oriented so its local X axis runs parallel to the
   * surface's longest edge, Y along the in-plane perpendicular, and Z along
   * the surface normal. Local AABB of the corners in that frame + 1 m
   * padding on every axis (so flat surfaces — whose local bbox collapses to
   * zero in one axis — still produce a visible clip volume).
   *
   * Declared before the useEffect / early-return so TDZ ordering is safe;
   * the function is closure-stable per-render.
   */
  const repositionClipBoxAroundCorners = (corners: Array<[number, number, number]>) => {
    if (corners.length === 0) return;
    const cornerVecs = corners.map((c) => new THREE.Vector3(c[0], c[1], c[2]));

    // Longest edge → local X.
    let longestLen = -1;
    let xAxis = new THREE.Vector3(1, 0, 0);
    for (let i = 0; i < cornerVecs.length; i++) {
      const a = cornerVecs[i];
      const b = cornerVecs[(i + 1) % cornerVecs.length];
      const d = b.clone().sub(a);
      const l = d.length();
      if (l > longestLen) {
        longestLen = l;
        xAxis = d.normalize();
      }
    }

    // Polygon normal via Newell's method → local Z. Falls back to world up
    // for degenerate (collinear) inputs.
    const zAxis = new THREE.Vector3();
    for (let i = 0; i < cornerVecs.length; i++) {
      const a = cornerVecs[i];
      const b = cornerVecs[(i + 1) % cornerVecs.length];
      zAxis.x += (a.y - b.y) * (a.z + b.z);
      zAxis.y += (a.z - b.z) * (a.x + b.x);
      zAxis.z += (a.x - b.x) * (a.y + b.y);
    }
    if (zAxis.lengthSq() < 1e-8) zAxis.copy(WORLD_UP);
    else zAxis.normalize();

    // Re-orthogonalise: y = z × x, then x = y × z so the basis is exactly
    // orthonormal even if the polygon isn't perfectly planar.
    const yAxis = new THREE.Vector3().crossVectors(zAxis, xAxis).normalize();
    xAxis = new THREE.Vector3().crossVectors(yAxis, zAxis).normalize();

    const quat = new THREE.Quaternion().setFromRotationMatrix(
      new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis),
    );

    // Local AABB around the polygon centroid in the rotated frame.
    const centroid = cornerVecs
      .reduce((acc, v) => acc.add(v), new THREE.Vector3())
      .divideScalar(cornerVecs.length);
    const invQ = quat.clone().invert();
    const localMin = new THREE.Vector3(Infinity, Infinity, Infinity);
    const localMax = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
    for (const v of cornerVecs) {
      const local = v.clone().sub(centroid).applyQuaternion(invQ);
      localMin.min(local);
      localMax.max(local);
    }

    const halfSize: [number, number, number] = [
      (localMax.x - localMin.x) / 2 + CLIPBOX_AUTO_PADDING_M,
      (localMax.y - localMin.y) / 2 + CLIPBOX_AUTO_PADDING_M,
      (localMax.z - localMin.z) / 2 + CLIPBOX_AUTO_PADDING_M,
    ];

    // Shift world centre so the box is centred on the local AABB midpoint
    // (handles non-symmetric polygons where centroid ≠ AABB midpoint).
    const localMid = localMin.clone().add(localMax).multiplyScalar(0.5);
    const worldOffset = localMid.applyQuaternion(quat);
    const center: [number, number, number] = [
      centroid.x + worldOffset.x,
      centroid.y + worldOffset.y,
      centroid.z + worldOffset.z,
    ];

    setClipBoxState((prev) => ({
      ...prev,
      center,
      halfSize,
      rotation: [quat.x, quat.y, quat.z, quat.w],
      enabled: true,
    }));
  };

  // Auto-position the clipbox the moment we have a draft rectangle (entering
  // the adjust phase, or the user just hit Finalize in many-points mode).
  // We do this here — while the surface tool is still active — instead of in
  // handleOk, so the user can interact with the clipbox handles to refine the
  // clip volume *before* committing the surface. After save the clipbox stays
  // wherever the user left it; we don't disable it on tool exit because the
  // user generally wants to keep clipping the scene for downstream deviation.
  useEffect(() => {
    if (!mode.enabled) {
      clipboxAutoPositionedRef.current = false;
      return;
    }
    if (mode.phase !== 'adjusting') return;
    if (!mode.draftCorners) return;
    if (clipboxAutoPositionedRef.current) return;
    clipboxAutoPositionedRef.current = true;
    repositionClipBoxAroundCorners(mode.draftCorners);
    // repositionClipBoxAroundCorners closes over setClipBoxState (a stable
    // jotai setter) and a constant; listing it would cause spurious re-runs.
    // The real trigger is the phase transition to 'adjusting'.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode.enabled, mode.phase, mode.draftCorners]);

  if (!mode.enabled) return null;

  const status =
    mode.phase === 'picking-1'
      ? t('addSurfaceTool.pickFirstCorner')
      : mode.phase === 'picking-2'
        ? t('addSurfaceTool.pickSecondCorner')
        : mode.phase === 'picking-3'
          ? mode.polygonMode
            ? t('addSurfaceTool.pickNextOrFinalize', { count: mode.pickedPoints.length })
            : t('addSurfaceTool.pickThirdCorner')
          : mode.phase === 'picking-n'
            ? t('addSurfaceTool.pickNextOrFinalize', { count: mode.pickedPoints.length })
            : t('addSurfaceTool.adjusting');

  const isAdjusting = mode.phase === 'adjusting';
  const isPicking =
    mode.phase === 'picking-1' ||
    mode.phase === 'picking-2' ||
    mode.phase === 'picking-3' ||
    mode.phase === 'picking-n';
  // Polygon-mode toggle is locked once the user starts clicking — flipping it
  // mid-flow would invalidate the partial state (rectangle wants exactly 3,
  // polygon wants ≥3 with explicit Finalize). They can Cancel to start over.
  const polygonToggleLocked = mode.pickedPoints.length > 0 || isAdjusting;
  const canUndo = isPicking && mode.pickedPoints.length > 0;
  // Finalize is the polygon-mode commit button. Available once we have
  // enough vertices (≥3) AND we're in the polygon-mode picking flow.
  const canFinalize = mode.polygonMode && isPicking && mode.pickedPoints.length >= 3;

  const reset = () => {
    setMode({ ...DEFAULT_ADD_SURFACE_MODE });
  };

  const handleCancel = () => {
    // The drawer reopens automatically once addSurfaceMode resets, because the
    // isReEditingThisSurface guard in FeatureProperties flips back to false.
    reset();
  };

  /**
   * Pop the most recent picked point. Rewinds the phase to whichever pick the
   * user is now waiting on. Works in both rectangle (3-pick) and polygon (N-pick)
   * flows. Does nothing once we're in the adjust phase — at that point the
   * rectangle is built and editing is via handles, not via undo.
   */
  const handleUndo = () => {
    if (!isPicking) return;
    if (mode.pickedPoints.length === 0) return;
    const next = mode.pickedPoints.slice(0, -1);
    const nextPhase: typeof mode.phase =
      next.length === 0
        ? 'picking-1'
        : next.length === 1
          ? 'picking-2'
          : next.length === 2
            ? 'picking-3'
            : 'picking-n';
    setMode((prev) => ({ ...prev, pickedPoints: next, phase: nextPhase }));
  };

  /**
   * Many-points commit. The output is still a rectangle (4 corners) — the
   * user just gets to define it via more than 3 clicks for noisier surfaces
   * where 3-point picks are awkward. `rectFromManyPicks` orients the plane
   * from picks 1–3 (respecting horizontal/vertical lock semantics) and then
   * expands the rectangle to be the bounding box of all picks projected
   * into that plane's basis. From there the existing rectangle-handle UX
   * + handleOk save path takes over unchanged.
   */
  const handleFinalize = () => {
    if (!canFinalize) return;
    const picks = mode.pickedPoints.map((p) => new THREE.Vector3(...p));
    const fitted = rectFromManyPicks(picks, {
      horizontalLock: mode.horizontalLock,
      verticalLock: mode.verticalLock,
    });
    if (!fitted) {
      toast.error(t('addSurfaceTool.collinearPicks'));
      return;
    }
    const corners = rectToPolygonCoords(fitted).slice(0, 4) as Array<[number, number, number]>;
    setMode((prev) => ({
      ...prev,
      phase: 'adjusting',
      draftCorners: corners,
    }));
  };

  const projectCornersToWGS84 = async (
    corners: Array<[number, number, number]>,
  ): Promise<Array<[number, number, number]> | null> => {
    if (!srid || srid === -1) return null;
    try {
      const projected = await Promise.all(corners.map((c) => project(c, `EPSG:${srid}`)));
      // project() returns undefined when proj4 isn't loaded yet — bail in that case.
      if (projected.some((p) => !p)) return null;
      return projected as Array<[number, number, number]>;
    } catch {
      return null;
    }
  };

  const buildPolygonGeometry = (
    corners: Array<[number, number, number]>,
    targetSrid: number,
  ) => ({
    type: 'Polygon' as const,
    crs: {
      type: 'name' as const,
      properties: { name: `urn:ogc:def:crs:EPSG::${targetSrid}` },
    },
    coordinates: [[...corners, corners[0]]],
  });

  // Width/height/uAxis/vAxis/normal/origin are all derivable from the 4 polygon
  // corners on demand (rectFromPolygonCoords). Storing them would duplicate the
  // source of truth and clutter the properties panel. Only persist what's
  // user-meaningful: the featureType discriminator + a friendly Name.
  const buildSurfaceProperties = (existing: Record<string, any> = {}) => ({
    ...existing,
    featureType: SURFACE_FEATURE_TYPE,
    Name: existing.Name ?? `surface ${Math.random().toString(36).slice(2, 8)}`,
  });

  const handleOk = async () => {
    if (!mode.draftCorners || !mode.targetLayerId) return;
    const corners = mode.draftCorners;
    // Re-edit: keep the user's existing attribute properties (Name, etc.).
    // Create: pass nothing — Name auto-fills with "surface <randomId>".
    const existing = mode.editFeatureId
      ? (selectedFeature?.properties as Record<string, any> | undefined) ?? {}
      : {};
    const properties = buildSurfaceProperties(existing);
    const wgs84Corners = await projectCornersToWGS84(corners);

    if (mode.editFeatureId) {
      const toastId = toast.loading(t('addSurfaceTool.savingSurface'));
      try {
        const input: any = {
          id: mode.editFeatureId,
          geometry: buildPolygonGeometry(corners, srid),
          properties,
        };
        if (wgs84Corners) {
          input.geometryWGS84 = buildPolygonGeometry(wgs84Corners, 4326);
        }
        const result = await execute(updateFeatureMutation, { input });
        const updated = result?.updateFeature;
        toast.success(t('addSurfaceTool.surfaceSaved'), { id: toastId });
        queryClient.invalidateQueries({ queryKey: ['features', mode.targetLayerId] });
        // Clipbox was already auto-positioned when we entered the adjust
        // phase (and possibly fine-tuned by the user since); we deliberately
        // don't re-position on save so the user's edits are preserved.
        // Use the server's exact response so selectedFeature.geometry has the same
        // shape as a freshly-fetched feature (no synthetic `crs` field). Avoids the
        // round-trip mismatch through wkx + toGeoJSON which strips `crs`.
        setSelectedFeature((prev) =>
          prev && updated
            ? {
                ...prev,
                geometry: updated.geometry,
                geometryWGS84: updated.geometryWGS84,
                properties: updated.properties,
              }
            : prev,
        );
        reset();
      } catch (e: any) {
        toast.error(e?.message || t('addSurfaceTool.failedSaveSurface'), { id: toastId });
      }
      return;
    }

    const toastId = toast.loading(t('addSurfaceTool.creatingSurface'));
    try {
      const input: any = {
        layerId: mode.targetLayerId,
        geometry: buildPolygonGeometry(corners, srid),
        properties,
      };
      if (wgs84Corners) {
        input.geometryWGS84 = buildPolygonGeometry(wgs84Corners, 4326);
      }
      const result = await execute(createFeatureMutation, { input });
      if (!result?.createFeature) throw new Error(t('addSurfaceTool.failedCreateSurface'));

      toast.success(t('addSurfaceTool.surfaceCreated'), { id: toastId });
      queryClient.invalidateQueries({ queryKey: ['features', mode.targetLayerId] });
      // Clipbox was already auto-positioned when we entered the adjust phase.
      // No reposition on save — preserve any tweaks the user made via the
      // clipbox handles while still in the surface tool.

      const newFeature = result.createFeature;
      reset();
      selectFeatureForEditing({
        ...newFeature,
        layerId: mode.targetLayerId,
        onSave: createUpdateHandler(newFeature.id, mode.targetLayerId),
      });
    } catch (e: any) {
      toast.error(e?.message || t('addSurfaceTool.failedCreateSurface'), { id: toastId });
    }
  };

  const snapCameraToView = (axis: 'top' | 'front' | 'side') => {
    if (!isAdjusting || !mode.draftCorners || !cameraControlsRef.current) return;
    const built = rectFromPolygonCoords([...mode.draftCorners, mode.draftCorners[0]] as [
      number,
      number,
      number,
    ][]);
    if (!built) return;
    const center = built.A.clone().add(built.B).add(built.C).add(built.D).multiplyScalar(0.25);
    const diag = built.C.clone().sub(built.A).length();
    // Camera distance from the surface. Two regimes:
    //
    // - **Perspective**: distance affects framing because object size scales
    //   with depth. Use `max(diag * 1.5, 5)` so a small surface still gets
    //   a usable viewing distance (~5 m floor) and a big surface backs the
    //   camera off proportionally.
    //
    // - **Ortho**: distance does NOT affect framing — only `camera.zoom`
    //   does, so a small `dist` value is wasted on view content. What
    //   distance DOES affect is (a) `cameraControls.distance`, which drives
    //   orbit pivot scale and pan feel, and (b) the near/far clip planes
    //   (`far = 2000` is measured forward of the camera, so a camera
    //   sitting 5 m in front of a wall clips most of the scene behind it).
    //   1000 m parks the camera well clear of any project-scale geometry,
    //   keeps the entire scene inside the [near, far] slab, and gives the
    //   controls a comfortable orbit radius that matches how the rest of
    //   Viewer feels in ortho mode.
    const dist = orthoCamera ? 1000 : Math.max(diag * 1.5, 5);
    // top: world-up (look down at the surface from above the scene, regardless of orientation)
    // front: surface normal (look face-on at the surface)
    // side: surface uAxis (look edge-on along the width)
    const baseDir =
      axis === 'top' ? WORLD_UP.clone() : axis === 'front' ? built.normal.clone() : built.uAxis.clone();
    const sign = viewSign[axis];
    const dir = baseDir.multiplyScalar(sign);
    // Flip for next click so repeat-press toggles to the opposite side. State
    // (not a ref) so the toolbar icon swap re-renders to preview the next
    // direction.
    setViewSign((prev) => ({ ...prev, [axis]: (-sign) as 1 | -1 }));
    const camPos = center.clone().addScaledVector(dir, dist);
    cameraControlsRef.current.setLookAt(
      camPos.x,
      camPos.y,
      camPos.z,
      center.x,
      center.y,
      center.z,
      true,
    );
    // Tell Viewer's position-sync effect to skip the next pass — it would otherwise
    // yank the camera back using stale viewerState.position. Then update the
    // anchor position to the surface center so any future syncs (e.g. when the
    // user toggles ortho on/off) preserve the snapped view.
    skipPositionSyncRef.current = true;
    setPosition(center.clone());
  };

  return (
    <FloatingToolbar status={status}>
      <FloatingToolbarButton
        icon={<Path />}
        tooltip={
          mode.polygonMode
            ? t('addSurfaceTool.polygonModeOn')
            : t('addSurfaceTool.polygonModeOff')
        }
        active={mode.polygonMode}
        // Locked once picking has started — flipping mid-flow would mix
        // 3-pick auto-build and N-pick Finalize rules in invalid ways.
        disabled={polygonToggleLocked}
        onClick={() =>
          setMode((prev) => ({
            ...prev,
            polygonMode: !prev.polygonMode,
          }))
        }
      />
      {/* Horizontal/vertical AB locks apply in both 3-pick and N-pick flows
          because the N-pick fit (`rectFromManyPicks`) re-uses the first 3
          picks to orient the plane, with the same lock semantics. */}
      <FloatingToolbarButton
        icon={mode.horizontalLock ? <MoveHorizontalSolid /> : <MoveHorizontal />}
        tooltip={
          mode.horizontalLock
            ? t('addSurfaceTool.horizontalLockOn')
            : t('addSurfaceTool.horizontalLockOff')
        }
        active={mode.horizontalLock}
        onClick={() =>
          setMode((prev) => ({
            ...prev,
            horizontalLock: !prev.horizontalLock,
            // Vertical lock requires AB horizontal; turning off horizontal also
            // disables vertical to keep state consistent.
            verticalLock: prev.horizontalLock ? false : prev.verticalLock,
          }))
        }
      />
      <FloatingToolbarButton
        icon={mode.verticalLock ? <MoveVerticalSolid /> : <MoveVertical />}
        tooltip={
          mode.verticalLock
            ? t('addSurfaceTool.verticalLockOn')
            : t('addSurfaceTool.verticalLockOff')
        }
        active={mode.verticalLock}
        onClick={() =>
          setMode((prev) => ({
            ...prev,
            verticalLock: !prev.verticalLock,
            // Vertical lock implies horizontal AB, so auto-engage horizontalLock.
            horizontalLock: !prev.verticalLock ? true : prev.horizontalLock,
          }))
        }
      />
      <FloatingToolbarSeparator />
      <FloatingToolbarButton
        icon={<Undo />}
        tooltip={t('addSurfaceTool.undoPick')}
        disabled={!canUndo}
        onClick={handleUndo}
      />
      {/* Finalize commits a polygon (N≥3 vertices) and transitions to the
          adjust phase. Only meaningful in polygon mode — rectangle mode
          auto-commits after the third pick. */}
      {mode.polygonMode && (
        <FloatingToolbarButton
          icon={<CheckSquare />}
          tooltip={t('addSurfaceTool.finalizePolygon')}
          disabled={!canFinalize}
          onClick={handleFinalize}
        />
      )}
      <FloatingToolbarSeparator />
      {/* Each view button shows a directional arrow that previews where
          the camera will move on the *next* click — so the user can see at
          a glance which side they're about to flip to. The icon flips
          after each click because `viewSign` is state, not a ref.
          - Top axis (vertical, Z): ↓ when next click puts you above
            looking down, ↑ when next click puts you below looking up.
          - Front axis (along surface normal): → / ← as a visual toggle
            between the two opposing camera positions on the normal axis.
          - Side axis (along surface u-axis): same → / ← toggle but for
            the perpendicular horizontal direction. */}
      <FloatingToolbarButton
        icon={viewSign.top === 1 ? <ArrowDownSquare /> : <ArrowUpSquare />}
        tooltip={
          viewSign.top === 1 ? t('addSurfaceTool.viewTop') : t('addSurfaceTool.viewBottom')
        }
        disabled={!isAdjusting}
        onClick={() => snapCameraToView('top')}
      />
      <FloatingToolbarButton
        icon={viewSign.front === 1 ? <ArrowRightSquare /> : <ArrowLeftSquare />}
        tooltip={
          viewSign.front === 1 ? t('addSurfaceTool.viewFront') : t('addSurfaceTool.viewBack')
        }
        disabled={!isAdjusting}
        onClick={() => snapCameraToView('front')}
      />
      <FloatingToolbarButton
        icon={viewSign.side === 1 ? <ArrowLeftSquare /> : <ArrowRightSquare />}
        tooltip={
          viewSign.side === 1 ? t('addSurfaceTool.viewSide') : t('addSurfaceTool.viewSideOpposite')
        }
        disabled={!isAdjusting}
        onClick={() => snapCameraToView('side')}
      />
      <FloatingToolbarSeparator />
      <FloatingToolbarButton
        icon={<CameraSolid />}
        tooltip={t('addSurfaceTool.toggleOrtho')}
        active={orthoCamera}
        onClick={() => setOrthoCamera(!orthoCamera)}
      />
      <FloatingToolbarSeparator />
      <FloatingToolbarButton
        icon={<Check />}
        tooltip={mode.editFeatureId ? t('addSurfaceTool.save') : t('addSurfaceTool.ok')}
        disabled={!isAdjusting}
        intent='primary'
        onClick={handleOk}
      />
      <FloatingToolbarButton
        icon={<Trash />}
        tooltip={t('addSurfaceTool.cancel')}
        intent='danger'
        onClick={handleCancel}
      />
    </FloatingToolbar>
  );
};
